import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/pro-light-svg-icons/faTrash';
import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons/faTriangleExclamation';
import { TypedMemo } from '../../../common/types';
import { ConfirmDialog } from '../../../common/views/modals/confirmDialog/ConfirmDialog';
import { ConfirmDialogIconMark } from '../../../common/views/modals/confirmDialog/ConfirmDialogMark';
import { useConfirmWhileBusy } from '../../../common/views/modals/confirmDialog/useConfirmWhileBusy';
import { ApiAccessToken } from './useApiAccessTokens';
import { ApiCopyButton } from './ApiCopyButton';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the API tab; each part uses its own slice of it */
import styles from './apiAccess.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ApiAccessTokenRowProps {
  token: ApiAccessToken;
  onRevoke: (id: number) => void;
  isRevoking: boolean;
}

const ApiAccessTokenRowComponent: React.FC<ApiAccessTokenRowProps> = ({ token, onRevoke, isRevoking }) => {
  const intl = useIntl();
  const revoke = useCallback(() => onRevoke(token.id), [onRevoke, token.id]);
  // design: dialogs a4. The dialog stays open with a spinner until the revoke ends.
  const dialog = useConfirmWhileBusy({ isBusy: isRevoking, onConfirm: revoke });

  const revokeLabel = intl.formatMessage(
    { id: 'app.sml.settings.apiAccess.list.revokeNamed', defaultMessage: 'Revoke {name}' },
    { name: token.name }
  );

  return (
    <div className={styles.row}>
      <div className={styles.rowName}>
        <span>{token.name}</span>
      </div>
      <div className={styles.rowPrefix} data-clarity-mask="true">
        <code>{token.tokenPrefix}&hellip;</code>
        <ApiCopyButton
          text={token.tokenPrefix}
          label={intl.formatMessage({
            id: 'app.sml.settings.apiAccess.list.copyPrefix',
            defaultMessage: 'Copy the token prefix',
          })}
        />
      </div>
      <div className={`${styles.rowCell} ${styles.columnCreated}`}>
        {intl.formatDate(token.createdAt, { year: 'numeric', month: 'short', day: 'numeric' })}
      </div>
      <div className={`${styles.rowCell}${token.lastUsedAt === null ? ` ${styles.rowCellNever}` : ''}`}>
        {token.lastUsedAt === null
          ? intl.formatMessage({ id: 'app.sml.settings.apiAccess.list.neverUsed', defaultMessage: 'Never' })
          : intl.formatDate(token.lastUsedAt, { year: 'numeric', month: 'short', day: 'numeric' })}
      </div>
      <div className={styles.rowEnd}>
        <button
          type="button"
          className={`${styles.iconButton} ${styles.iconButtonDanger}`}
          onClick={dialog.open}
          disabled={isRevoking}
          title={revokeLabel}
          aria-label={revokeLabel}
        >
          <FontAwesomeIcon icon={faTrash} />
        </button>
        <ConfirmDialog
          isOpen={dialog.isOpen}
          tone="danger"
          mark={<ConfirmDialogIconMark tone="danger" icon={faTriangleExclamation} />}
          cancelLabel={intl.formatMessage({
            id: 'app.sml.settings.apiAccess.revoke.cancel',
            defaultMessage: 'Cancel',
          })}
          title={intl.formatMessage({
            id: 'app.sml.settings.apiAccess.revoke.confirmTitle',
            defaultMessage: 'Revoke this token?',
          })}
          description={intl.formatMessage(
            {
              id: 'app.sml.settings.apiAccess.revoke.confirmDescription',
              defaultMessage: 'Any app using "{name}" will stop working right away. This cannot be undone.',
            },
            { name: token.name }
          )}
          confirmLabel={intl.formatMessage({
            id: 'app.sml.settings.apiAccess.revoke.confirmCta',
            defaultMessage: 'Revoke token',
          })}
          isBusy={dialog.isRunning}
          onConfirm={dialog.confirm}
          onCancel={dialog.cancel}
        />
      </div>
    </div>
  );
};

export const ApiAccessTokenRow = TypedMemo(ApiAccessTokenRowComponent);
